import {
    CameraCreationItem,
    CameraCreationStatus,
    CameraValidationStatus,
} from '@/types/camera';
import { SourceStatus } from '@/types/source';

export { CameraCreationStatus, CameraValidationStatus };
export type { CameraCreationItem };

export type FileFingerprint = string;

export enum FileUploadStatus {
    PENDING = 'pending',
    UPLOADING = 'uploading',
    COMPLETED = 'completed',
    FAILED = 'failed',
}

export interface FileUploadItem {
    id: string;
    fingerprint: FileFingerprint;
    file: File;
    fileName: string;
    fileSize: number;
    faceRecognition: boolean;
    licensePlateRecognition: boolean;
    opticalCharacterRecognition: boolean;
    analysisFramesPerSecond: number;
    status: FileUploadStatus;
    progress: number;
    error?: string;
    retryCount: number;
    targetFolderId?: string | null;
    targetReportId?: string | null;
    createdSourceId?: string;
    sourceStatus?: SourceStatus;
    sourceProgress?: number;
}

export type FileUploadItemAwaitingBackendTerminal = FileUploadItem & {
    createdSourceId: string;
};

export function isFileUploadItemAwaitingBackendTerminal(
    item: FileUploadItem,
): item is FileUploadItemAwaitingBackendTerminal {
    if (item.status !== FileUploadStatus.COMPLETED) {
        return false;
    }
    if (!item.createdSourceId) {
        return false;
    }
    if (
        item.sourceStatus === SourceStatus.COMPLETED ||
        item.sourceStatus === SourceStatus.ERROR
    ) {
        return false;
    }
    return true;
}

const PREPARING_FILE_UPLOAD_STATUSES = new Set<FileUploadStatus>([
    FileUploadStatus.PENDING,
    FileUploadStatus.UPLOADING,
    FileUploadStatus.FAILED,
]);

export const PREPARING_SOURCE_STATUSES = new Set<SourceStatus>([
    SourceStatus.UPLOADING,
    SourceStatus.PENDING,
    SourceStatus.FIXING,
]);

export const FINISHED_SOURCE_STATUSES = new Set<SourceStatus>([
    SourceStatus.COMPLETED,
    SourceStatus.ERROR,
]);

export function isFileUploadItemBeingPrepared(item: FileUploadItem): boolean {
    if (PREPARING_FILE_UPLOAD_STATUSES.has(item.status)) return true;
    if (!item.createdSourceId) return true;
    if (item.sourceStatus === undefined) return true;
    return PREPARING_SOURCE_STATUSES.has(item.sourceStatus);
}

export type SourceCreationItem =
    | { type: 'file'; data: FileUploadItem }
    | { type: 'camera'; data: CameraCreationItem };

export interface SourceCreationDrawerState {
    isExpanded: boolean;
}
